<template>
  <div
    class="auth-wrapper auth-v1 px-2"
    :class="{ 'mfa-auth-scroll': mfaStep }"
  >
    <div class="auth-inner py-2">

      <mfa-challenge-form
        v-if="mfaStep === 'challenge'"
        @authenticated="onMfaAuthenticated"
        @cancel="onMfaCancel"
        @locked="onMfaLocked"
      />
      <mfa-setup-form
        v-else-if="mfaStep === 'setup'"
        :continuing="finishingLogin"
        @completed="onMfaAuthenticated"
        @cancel="onMfaCancel"
      />

      <!-- Login v1 -->
      <b-card
        v-else
        class="mb-0"
      >
        <b-link class="brand-logo">
          <ok-logo />
          <h2 class="brand-text text-primary ml-1" />
        </b-link>
        <error-alert
          v-if="$route.hash === '#redirected'"
          :error="$t('auth.please-register-to-continue')"
          variant="warning"
        />
        <error-alert
          v-if="error"
          :error="error"
        />
        <b-card-title class="mb-1">
          {{ $t('auth.welcome-back') }} 👋
        </b-card-title>
        <b-card-text class="mb-2">
          {{ $t('auth.login-hint') }}
        </b-card-text>

        <!-- form -->
        <validation-observer
          ref="loginForm"
          #default="{invalid}"
        >
          <b-form
            class="auth-login-form mt-2"
            @submit.prevent="submit"
          >
            <ok-text-input
              v-model="userEmail"
              dir="ltr"
              input-class="text-left"
              :label="$t('fields.email')"
              :is-required="true"
              hide-required-asterisk
            />
            <!-- password -->
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label class="auth-label">{{ $t('fields.password') }}</label>
                <b-link :to="{name:'auth:forget'}">
                  <small>{{ $t('auth.forgot-pass') }}</small>
                </b-link>
              </div>
              <validation-provider
                #default="{ errors }"
                name="Password"
                rules="required"
              >
                <password-input
                  v-model="password"
                  :label="$t('fields.password')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- checkbox -->
            <b-form-group>
              <b-form-checkbox
                id="remember-me"
                v-model="remember"
                name="checkbox-1"
              >
                {{ $t('auth.remember-me') }}
              </b-form-checkbox>
            </b-form-group>

            <!-- submit button -->
            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="invalid || submitting"
            >
              {{ $t('auth.signin') }}
              <b-spinner
                v-if="submitting"
                small
                class="mr-50"
              />
            </b-button>
          </b-form>
        </validation-observer>

        <b-card-text class="text-center mt-2">
          <span>{{ $t('auth.no-account') }} </span>
          <b-link :to="{name:'register', query: $route.query}">
            <span>{{ $t('auth.create-account') }}</span>
          </b-link>
        </b-card-text>

        <div class="divider my-2">
          <div class="divider-text">
            {{ $t('general.or') }}
          </div>
        </div>

        <!-- social button -->
        <social-auth
          v-if="socialAuth"
          @error="error=$event"
          @clear-error="error=null"
        />
      </b-card>
      <!-- /Login v1 -->
    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BLink,
  BFormGroup,
  BFormCheckbox,
  BCardText,
  BCardTitle,
  BForm,
  BButton,
  BCard,
  BSpinner,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import OkTextInput from '@/@okmenu/components/inputs/OkTextInput'
import PasswordInput from '@/@okmenu/components/inputs/PasswordInput'
import OkLogo from '@/@okmenu/icons/OkLogo'
import SocialAuth from '@/views/auth/social-auth'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import StorageService from '@/services/storage-service'
import MfaChallengeForm from '@/views/auth/mfa/MfaChallengeForm'
import MfaSetupForm from '@/views/auth/mfa/MfaSetupForm'

export default {
  components: {
    ErrorAlert,
    MfaChallengeForm,
    MfaSetupForm,
    SocialAuth,
    OkLogo,
    PasswordInput,
    OkTextInput,
    BButton,
    BForm,
    BFormGroup,
    BCard,
    BCardTitle,
    BSpinner,
    BLink,
    BCardText,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      remember: true,
      password: '',
      userEmail: '',
      submitting: false,
      required,
      email,
      error: null,
      socialAuth: false,
      mfaStep: null,
      finishingLogin: false,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    async submit() {
      this.error = null
      const isValid = await this.$refs.loginForm.validate()

      if (!isValid) return

      const credentials = {
        email: this.userEmail,
        password: this.password,
        oneTime: !this.remember,
      }
      this.submitting = true
      this.$store.dispatch('auth/LOGIN', credentials)
        .then(result => {
          if (result?.invalidMfa) {
            this.error = this.$t('auth.mfa-token-missing')
            this.submitting = false
            return
          }
          if (result?.mfaPending) {
            this.mfaStep = result.mode
            this.error = null
            this.submitting = false
            return
          }
          this.enterDashboard(result.user)
        })
        .catch(err => {
          this.$notifyError(err)
          this.error = err
          this.submitting = false
        })
    },
    enterDashboard(user) {
      this.$notifySuccess(this.$t('auth.welcome-back-user', [user.name]))
      const pendingRoute = StorageService.getItem('okmenu_route_after_auth', null, -1)
      this.$router.push(pendingRoute ?? { name: 'admin:home' }).catch(() => {})
    },
    async onMfaAuthenticated(payload) {
      if (this.finishingLogin) return
      this.finishingLogin = true
      try {
        const user = await this.$store.dispatch('auth/FINISH_LOGIN', { response: payload })
        this.enterDashboard(user)
      } catch (err) {
        this.finishingLogin = false
        this.$notifyError(err)
      }
    },
    onMfaCancel() {
      this.mfaStep = null
      this.submitting = false
      this.finishingLogin = false
      this.$store.dispatch('auth/CLEAR_MFA')
    },
    onMfaLocked() {
      this.mfaStep = null
      this.submitting = false
      this.error = this.$t('auth.mfa-locked')
    },
  },
  metaInfo() {
    return {
      title: this.$t('auth.login'),
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.auth-wrapper.mfa-auth-scroll {
  overflow: auto;
  align-items: center;
}
</style>
